import {
  Activity,
  Archive,
  ArrowRight,
  Ban,
  Bell,
  Bug,
  Globe,
  Play,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { KanameMark } from '@/components/brand/KanameMark';
import { ErrorBoundary, RootCrash } from '@/components/ErrorBoundary';
import { BootScene } from '@/components/layout/shell/BootScene';
import type { EntranceFrom } from '@/components/layout/shell/entrance';
import { ShellEntrance } from '@/components/layout/shell/ShellEntrance';
import { ConnectionStrip } from '@/components/layout/workspace/ConnectionStrip';
import { StateFrame } from '@/components/layout/workspace/StateFrame';
import { TabCrashDot } from '@/components/layout/workspace/TabStrip';
import { errorDetails } from '@/lib/errorReport';
import {
  Badge,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  EntityChip,
  HoldToConfirm,
  Input,
  PageHeader,
  SheetContent,
  Skeleton,
  Switch,
  TableSkeleton,
  Textarea,
  ToastProvider,
  Toolbar,
  cn,
  useToast,
} from '@/components/ui';
import { Code, Section, SubTitle } from './Section';

/*
  Оболочка и действия — плитки примитивов: окно и шторка, тост с отменой, чипы-фильтры,
  шапка с панелью, многострочное поле, удержание двух размеров; ожидание и ошибки (R42) — вкладка целиком
  (StateFrame), сбой вкладки и падение интерфейса, первая загрузка сайта (BootScene → ShellEntrance brisk),
  нет связи (ConnectionStrip).
*/

function Row({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SubTitle>{title}</SubTitle>
        {note && <span className="text-xs text-fg-muted">{note}</span>}
      </div>
      {children}
    </div>
  );
}

function ToastDemo() {
  const { toast } = useToast();
  const [archived, setArchived] = useState(0);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="secondary"
        leftIcon={<Archive />}
        onClick={() => {
          setArchived((n) => n + 1);
          toast({
            title: 'Упоминание в архиве',
            action: { label: 'Отменить', undo: true, onClick: () => setArchived((n) => Math.max(0, n - 1)) },
          });
        }}
      >
        В архив
      </Button>
      <Button
        variant="ghost"
        onClick={() => toast({ title: 'Ответ отправлен в #support-l2', tone: 'success' })}
      >
        Просто сообщение
      </Button>
      <span className="text-sm text-fg-muted">В архиве: {archived}</span>
    </div>
  );
}

function ChipDemo() {
  const [on, setOn] = useState<Set<string>>(new Set(['Стандарт']));
  return (
    <div className="flex flex-wrap gap-2">
      {[
        ['Экономный', 10],
        ['Стандарт', 21],
        ['Бизнес', 9],
      ].map(([label, count]) => (
        <Chip
          key={label}
          selected={on.has(label as string)}
          count={count as number}
          onClick={() =>
            setOn((prev) => {
              const next = new Set(prev);
              if (next.has(label as string)) next.delete(label as string);
              else next.add(label as string);
              return next;
            })
          }
        >
          {label}
        </Chip>
      ))}
    </div>
  );
}

/* ---------- StateFrame — вкладка целиком (R42) ---------- */

/** Шаги образца первого за сеанс открытия приложения. */
const OPEN_STEPS = ['Права доступа', 'Код приложения', 'Меню', 'Первый вид'];
/** Темп образца — «Медленно» в «Основе»: шаг за 600 мс. */
const OPEN_STEP_MS = 600;

type FrameMode = 'loading' | 'stuck' | 'fail' | 'missing' | 'locked' | 'crashed';

const FRAME_MODES: { value: FrameMode; label: string }[] = [
  { value: 'loading', label: 'Загрузка' },
  { value: 'stuck', label: 'Дольше обычного' },
  { value: 'fail', label: 'Сбой на шаге' },
  { value: 'missing', label: 'Не найдено' },
  { value: 'locked', label: 'Нет доступа' },
  { value: 'crashed', label: 'Сбой вкладки' },
];

const SKELETON_COLUMNS = [
  { width: 'w-16' },
  { width: 'w-40' },
  { width: 'w-24' },
  { width: 'w-10', align: 'right' as const },
];

/** Один прогон образца: загрузка идёт сама, «Повторить» после сбоя проходит. */
function FrameSample({ mode }: { mode: FrameMode }) {
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState(false);
  const [retried, setRetried] = useState(false);
  const loads = mode === 'loading' || mode === 'stuck' || mode === 'fail';
  useEffect(() => {
    if (!loads || failed || done >= OPEN_STEPS.length || (mode === 'stuck' && done >= 1)) return undefined;
    const t = window.setTimeout(() => {
      if (mode === 'fail' && done === 2 && !retried) setFailed(true);
      else setDone((d) => d + 1);
    }, OPEN_STEP_MS);
    return () => window.clearTimeout(t);
  }, [loads, mode, done, failed, retried]);

  if (mode === 'missing')
    return (
      <StateFrame
        kind="missing"
        title="Узел host-999 не найден"
        description="Его могли вывести из работы или переименовать."
        action={{ label: 'К списку узлов' }}
        secondaryAction={{ label: 'Найти узел · Ctrl+K' }}
      />
    );
  if (mode === 'locked')
    return (
      <StateFrame
        kind="locked"
        title="Нет доступа к разделу «Журнал»"
        description="Раздел есть, но для этой роли закрыт. Доступ выдаёт владелец приложения."
        action={{ label: 'Запросить доступ', variant: 'primary' }}
        secondaryAction={{ label: 'Назад' }}
      />
    );
  if (mode === 'crashed')
    return (
      <StateFrame
        kind="crashed"
        title="В этой вкладке сбой"
        description="Остальные вкладки и приложения работают. Ошибка уже записана."
        action={{ label: 'Открыть заново', variant: 'primary' }}
        secondaryAction={{ label: 'Закрыть вкладку' }}
        details={'K-7F3A · TypeError\nCannot read properties of undefined (reading "nodes")'}
      />
    );
  return (
    <StateFrame
      kind={failed ? 'failed' : 'loading'}
      title={failed ? 'Не удалось открыть «Инфраструктуру»' : 'Открываем «Инфраструктуру»'}
      steps={OPEN_STEPS}
      done={done}
      failed={{ step: OPEN_STEPS[done], reason: 'сервер не ответил за 30 с.' }}
      action={
        failed
          ? {
              label: 'Повторить',
              variant: 'primary',
              onClick: () => {
                setRetried(true);
                setFailed(false);
              },
            }
          : { label: 'Повторить' }
      }
      secondaryAction={{ label: failed ? 'Назад' : 'Закрыть вкладку' }}
      details={failed ? 'K-7F3A · GET /api/apps/infra/menu — 504 за 30 с' : undefined}
    >
      <PageHeader
        title="Обзор"
        meta={<span>вид вошёл, его данные едут — заглушка той же формы (R31)</span>}
      />
      <div className="px-8">
        <TableSkeleton columns={SKELETON_COLUMNS} rows={4} />
      </div>
    </StateFrame>
  );
}

function StateFrameDemo() {
  const [run, setRun] = useState({ mode: 'loading' as FrameMode, key: 0 });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {FRAME_MODES.map((m) => (
          <Button
            key={m.value}
            size="sm"
            variant={run.mode === m.value ? 'secondary' : 'ghost'}
            aria-pressed={run.mode === m.value}
            onClick={() => setRun((r) => ({ mode: m.value, key: r.key + 1 }))}
          >
            {m.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<RotateCcw />}
          onClick={() => setRun((r) => ({ ...r, key: r.key + 1 }))}
        >
          Заново
        </Button>
      </div>
      {/* Мини-рамка вкладки: та же высота, что задаёт содержимому TabPanel. */}
      <div className="relative h-96 overflow-hidden rounded-lg border border-line bg-canvas">
        <div className="absolute inset-0 overflow-y-auto">
          <FrameSample key={run.key} mode={run.mode} />
        </div>
      </div>
      <p className="text-xs text-fg-muted">
        Загрузка: первые 400 мс пусто; дальше медальон — лепесток на пройденный шаг, строка шага с 1,5 с,
        секунды с 3 с, «Дольше обычного» и действия с 10 с. На «готово» шестой лепесток досаживается, медальон
        уходит, вид входит — и его данные сразу стоят заглушкой той же формы. Сбой: веер стоит на достигнутом,
        кольцо краснеет и толстеет; «Повторить» — шаги идут дальше с того же медальона.
      </p>
    </div>
  );
}

/* ---------- Сбой вкладки и падение интерфейса (R42) ---------- */

/** Образец ошибки для сцены падения в рамке. */
const SAMPLE_ERROR = new TypeError("Cannot read properties of undefined (reading 'nodes')");

/** Вид, который падает по кнопке: ловит настоящая граница — та же, что у вкладок «Основы». */
function FragileView({ broken }: { broken: boolean }) {
  if (broken) throw new TypeError('Проба: вид уронили кнопкой в галерее');
  return (
    <>
      <PageHeader
        title="Узлы"
        meta={<span>вид работает — «Уронить вкладку» бросит исключение при отрисовке</span>}
      />
      <div className="px-8">
        <TableSkeleton columns={SKELETON_COLUMNS} rows={4} still />
      </div>
    </>
  );
}

/** Ярлык вкладки — как в TabStrip: активная сливается с содержимым, упавшая несёт точку. */
function TabSample({ title, active, broken }: { title: string; active?: boolean; broken?: boolean }) {
  return (
    <span
      role="presentation"
      aria-label={broken ? `${title} — сбой` : undefined}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-t-sm border border-b-0 px-3 text-xs',
        active
          ? 'border-line bg-canvas font-semibold text-fg'
          : 'border-transparent font-medium text-fg-muted',
      )}
    >
      <Activity className="size-3.5 shrink-0 text-fg-subtle" />
      {title}
      {broken && <TabCrashDot />}
    </span>
  );
}

function CrashDemo() {
  const [broken, setBroken] = useState(false);
  const [root, setRoot] = useState(false);
  // Падение всего интерфейса — по-настоящему: ошибку ловит корневая граница (main.tsx), «Попробовать снова»
  // собирает интерфейс заново.
  if (root) throw new TypeError('Проба: интерфейс уронили кнопкой в галерее');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Bug />}
          disabled={broken}
          onClick={() => setBroken(true)}
        >
          Уронить вкладку
        </Button>
        <Button size="sm" variant="ghost" leftIcon={<Bug />} onClick={() => setRoot(true)}>
          Уронить интерфейс
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col overflow-hidden rounded-lg border border-line bg-canvas">
          <div className="flex h-11 shrink-0 items-end gap-1 bg-tabs pl-2 shadow-hairline-b">
            <TabSample title="Узлы" active broken={broken} />
            <TabSample title="INC-2189" broken />
          </div>
          {/* Мини-рамка вкладки: своя граница, привязанная к ней, как у TabPanel. */}
          <div className="relative h-96">
            <div className="absolute inset-0 overflow-y-auto">
              <ErrorBoundary
                fallback={(error, reset) => (
                  <StateFrame
                    kind="crashed"
                    title="В этой вкладке сбой"
                    description="Остальные вкладки и приложения работают. Ошибка уже записана."
                    action={{
                      label: 'Открыть заново',
                      variant: 'primary',
                      onClick: () => {
                        setBroken(false);
                        reset();
                      },
                    }}
                    secondaryAction={{ label: 'Закрыть вкладку' }}
                    details={errorDetails(error)}
                  />
                )}
              >
                <FragileView broken={broken} />
              </ErrorBoundary>
            </div>
          </div>
        </div>
        {/* Сцена падения в рамке — та же, что встаёт на весь экран; голоса у образца нет. */}
        <div className="relative min-h-96 overflow-hidden rounded-lg">
          <RootCrash contained error={SAMPLE_ERROR} onReset={() => undefined} />
        </div>
      </div>
      <p className="text-xs text-fg-muted">
        Вкладка: у каждой своя граница ошибок — упала одна, остальные работают; на её месте «В этой вкладке
        сбой» (веер цел и неподвижен, кольцо медальона красное и толще), на ярлыке — точка, читалке — «—
        сбой». Ушли со сбойного адреса или «Открыть заново» — вкладка снова целая. Весь интерфейс: тёмная
        сцена, ядро краснеет скачком, одна тревога — и ядро останавливается; «Открыть без восстановления
        вкладок» забывает сохранённые вкладки и окна. Если упадёт и сама сцена — останется текст.
      </p>
    </div>
  );
}

/* ---------- BootScene — первая загрузка сайта (R42) ---------- */

/** Шаги образца — те же, что у «Первой загрузки» в «Основе»; шаг за 600 мс. */
const BOOT_STEPS = ['Kaname', 'Вход', 'Профиль и права', 'Сервисы и меню', 'Вкладки', 'Первый экран'];
const BOOT_STEP_MS = 600;

type BootMode = 'slow' | 'stuck' | 'fail';

const BOOT_MODES: { value: BootMode; label: string }[] = [
  { value: 'slow', label: 'Медленно' },
  { value: 'stuck', label: 'Дольше обычного' },
  { value: 'fail', label: 'Сбой на шаге 3' },
];

interface BootRunSample {
  key: number;
  mode: BootMode;
  /** Когда нажали — от этого считаются пороги (performance.now()). */
  at: number;
}

/** Оболочка в миниатюре: главное меню со знаком — дом ядра — и холст. */
function MiniShell({ landed }: { landed: boolean }) {
  return (
    <div aria-hidden className="absolute inset-0 flex gap-2 p-2">
      <div className="flex w-20 shrink-0 flex-col items-center gap-3 rounded-lg py-3 rail-surface">
        <span data-brand-home="" className="grid size-12 shrink-0 place-items-center">
          <KanameMark open={landed} className="size-10" />
        </span>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="size-8 rounded-sm bg-rail-hover" />
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-4 rounded-lg border border-line bg-canvas p-6">
        <Skeleton still className="h-6 w-48" />
        <Skeleton still className="w-72 max-w-full" />
        <div className="grid flex-1 grid-cols-2 gap-4">
          <Skeleton still className="h-full rounded-md" />
          <Skeleton still className="h-full rounded-md" />
        </div>
      </div>
    </div>
  );
}

/** Один прогон: шаги идут сами; «Дольше обычного» стоит на шаге 4, «Сбой» встаёт на шаге 3 — «Повторить» проходит. */
function BootSample({ run, offline }: { run: BootRunSample; offline: boolean }) {
  const [done, setDone] = useState(1);
  const [failed, setFailed] = useState(false);
  const [retried, setRetried] = useState(false);
  const [booting, setBooting] = useState(true);
  const [entrance, setEntrance] = useState<EntranceFrom | null>(null);
  const [landed, setLanded] = useState(false);
  const total = BOOT_STEPS.length;
  const stuck = run.mode === 'stuck' && done === 3 && !retried;
  useEffect(() => {
    if (!booting || failed || offline || stuck || done >= total) return undefined;
    const t = window.setTimeout(() => {
      if (run.mode === 'fail' && done === 2 && !retried) setFailed(true);
      else setDone((d) => d + 1);
    }, BOOT_STEP_MS);
    return () => window.clearTimeout(t);
  }, [booting, failed, offline, stuck, done, total, run.mode, retried]);
  return (
    <>
      <MiniShell landed={landed} />
      {booting && (
        <BootScene
          contained
          blank
          steps={BOOT_STEPS}
          done={done}
          failed={failed ? { reason: 'Сервер не ответил за 10 с' } : null}
          offline={offline}
          startedAt={run.at}
          onRetry={() => {
            setRetried(true);
            setFailed(false);
          }}
          onExit={() => {
            setBooting(false);
            setLanded(true);
          }}
          onReady={(from) => {
            setBooting(false);
            if (from) setEntrance(from);
            else setLanded(true);
          }}
        />
      )}
      {entrance && (
        <ShellEntrance
          contained
          pace="brisk"
          from={entrance}
          onLand={() => setLanded(true)}
          onDone={() => setEntrance(null)}
        />
      )}
    </>
  );
}

function BootDemo() {
  const [run, setRun] = useState<BootRunSample | null>(null);
  const [offline, setOffline] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {BOOT_MODES.map((m) => (
          <Button
            key={m.value}
            size="sm"
            variant={run?.mode === m.value ? 'secondary' : 'ghost'}
            aria-pressed={run?.mode === m.value}
            leftIcon={<Play />}
            onClick={() => {
              const at = performance.now();
              setRun((r) => ({ key: (r?.key ?? 0) + 1, mode: m.value, at }));
            }}
          >
            {m.label}
          </Button>
        ))}
        <Switch size="sm" checked={offline} onCheckedChange={setOffline} label="Нет связи" />
      </div>
      {/* Рамка — та же страница в миниатюре: сцена и переход живут внутри неё (contained). */}
      <div className="relative isolate mx-auto aspect-video w-full max-w-3xl overflow-hidden rounded-lg border border-line bg-frame">
        {run ? <BootSample key={run.key} run={run} offline={offline} /> : <MiniShell landed />}
      </div>
      <p className="text-xs text-fg-muted">
        Первые 400 мс — ничего; дальше тёмная сцена с живым ядром — тем же, что index.html показывает до
        загрузки скрипта. Лепесток — пройденный шаг, шестой — только на «готово»; подпись с 1,5 с, секунды с 3
        с, «Дольше обычного» и «Повторить» с 10 с. Сбой: веер стоит, ядро краснеет скачком, тревога, дышит
        вдвое чаще. Нет связи: веер затаил дыхание, ядро в тоне предупреждения. Готово: шестой лепесток
        досаживается, через 240 мс передача — ShellEntrance pace="brisk": сцена гаснет с 120-й мс, веер
        складывается, ядро за 520 мс летит домой, и знак меню раскрывается вокруг него. Знак добавляет к
        загрузке не больше 0,6 с.
      </p>
    </div>
  );
}

/* ---------- ConnectionStrip — нет связи (R42) ---------- */

function ConnectionDemo() {
  const [offline, setOffline] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex">
        <Switch size="sm" checked={offline} onCheckedChange={setOffline} label="Нет связи" />
      </div>
      <div className="flex flex-col overflow-hidden rounded-lg border border-line bg-canvas">
        <div className="flex h-11 shrink-0 items-end gap-1 bg-tabs pl-2 shadow-hairline-b">
          <TabSample title="Обзор" active />
          <TabSample title="host-338" />
        </div>
        <ConnectionStrip offline={offline} dataAt="14:32" />
        <PageHeader title="Обзор" meta={<span>вкладки и данные остаются как были</span>} />
        <div className="px-8 pb-6">
          <TableSkeleton columns={SKELETON_COLUMNS} rows={3} still />
        </div>
      </div>
      <p className="text-xs text-fg-muted">
        Строка под полосой вкладок основного окна (WorkspaceCanvas notice): на какое время данные и когда
        следующая попытка — через 2, 4, 8, 16 с, дальше раз в 30 с; «Повторить сейчас» — попытка сразу,
        крутилка в кнопке. Связь вернулась — 1,6 с «Связь восстановлена» в тоне «готово», и строка гаснет;
        тоста нет. Знак в меню о связи ничего не показывает.
      </p>
    </div>
  );
}

export function ShellSection() {
  return (
    <ToastProvider>
      <Section
        id="shell"
        title="Оболочка и действия"
        lead="Всплывающие слои, мгновенные действия с отменой, фильтры и полки страницы. Всё вместе в работе — на странице «Основа»."
      >
        <Row title="Dialog и Sheet" note={<Code>Dialog · DialogContent · SheetContent · DialogHeader</Code>}>
          <div className="flex flex-wrap gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="secondary" leftIcon={<Ban />}>
                  Окно с вводом
                </Button>
              </DialogTrigger>
              <DialogContent size="md">
                <DialogHeader
                  title="Заблокировать example.test"
                  description="Модалка — только там, где нужен ввод (R27)."
                />
                <div className="px-6 pb-6">
                  <Textarea label="Причина" placeholder="Например: номер заявки" />
                </div>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="secondary" leftIcon={<Bell />}>
                  Шторка справа
                </Button>
              </DialogTrigger>
              <SheetContent>
                <DialogHeader title="Уведомления" description="Шторка — та же модалка, прижатая к краю." />
              </SheetContent>
            </Dialog>
          </div>
        </Row>

        <Row title="Toast с «Отменить»" note={<Code>ToastProvider · useToast · Ctrl+Z</Code>}>
          <ToastDemo />
        </Row>

        <Row title="Chip — фильтр-переключатель" note={<Code>Chip selected count</Code>}>
          <ChipDemo />
        </Row>

        <Row
          title="PageHeader и Toolbar — полки страницы"
          note={<Code>PageHeader eyebrow status meta actions · Toolbar</Code>}
        >
          <div className="overflow-hidden rounded-md border border-line bg-canvas">
            <PageHeader
              eyebrow={
                <>
                  <EntityChip kind="location" name="Площадка Д" size="sm" />
                  <EntityChip kind="host" name="host-005" size="sm" />
                </>
              }
              title="example.test"
              status={
                <Badge variant="success" solid>
                  Работает
                </Badge>
              }
              meta={
                <>
                  <span>demo1</span>
                  <span>Тариф «Демо»</span>
                  <span>PHP 8.3</span>
                </>
              }
              actions={
                <Button variant="secondary" leftIcon={<Ban />}>
                  Заблокировать
                </Button>
              }
            />
            <Toolbar>
              <div className="w-64">
                <Input leadingIcon={<Search />} placeholder="Домен, владелец или логин" aria-label="Поиск" />
              </div>
              <Chip icon={<Globe />} count={40} selected>
                Все
              </Chip>
            </Toolbar>
          </div>
        </Row>

        <Row title="HoldToConfirm — два размера" note={<Code>size="sm" для строк таблиц (R11)</Code>}>
          <div className="flex flex-wrap items-center gap-3">
            <HoldToConfirm onConfirm={() => {}}>Удалить копию</HoldToConfirm>
            <HoldToConfirm size="sm" onConfirm={() => {}}>
              <Trash2 />
              Удалить
            </HoldToConfirm>
            <Button size="sm" variant="secondary">
              Соседняя кнопка sm
            </Button>
          </div>
        </Row>

        <Row
          title="StateFrame — вкладка целиком"
          note={
            <Code>kind · title · steps · done · failed · action · secondaryAction · details · children</Code>
          }
        >
          <StateFrameDemo />
          <Link
            to="/design/foundation/apps/infra/node/host-999"
            className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-brand-fg underline-offset-4 hover:underline"
          >
            Посмотреть в «Основе» — узел host-999
            <ArrowRight className="size-4" />
          </Link>
        </Row>

        <Row
          title="Сбой вкладки и падение интерфейса"
          note={<Code>ErrorBoundary onError · TabStrip crashed · TabCrashDot · RootCrash</Code>}
        >
          <CrashDemo />
        </Row>

        <Row
          title="BootScene — первая загрузка сайта"
          note={
            <Code>
              steps · done · failed · offline · onReady(from) → ShellEntrance pace="brisk" · contained
            </Code>
          }
        >
          <BootDemo />
        </Row>

        <Row
          title="ConnectionStrip — нет связи"
          note={<Code>offline · dataAt · onRetry · WorkspaceCanvas notice</Code>}
        >
          <ConnectionDemo />
        </Row>
      </Section>
    </ToastProvider>
  );
}
